'use client';

import type { HTMLAttributes, ReactNode } from 'react';
import styled from 'styled-components';

import type { BlockTemplate } from '@zoomit/editor';
import type { ValueOf } from '@zoomit/primitives';
import { Breakpoints, ContainerMaxWidth } from '@zoomit/primitives';

import { getBlockContainerType } from '../../../helpers/article/blocks';

type Props = {
  hasSidebar: boolean;
  template?: ValueOf<BlockTemplate>;
  children?: ReactNode;
} & HTMLAttributes<HTMLDivElement>;

const BlockContainer = (props: Props) => {
  const { hasSidebar, template, children, ...rest } = props;

  return (
    <Root
      {...rest}
      $hasSidebar={hasSidebar}
      $isFullWidth={template === 'full_width' && !hasSidebar}
      className={template === 'full_width' && !hasSidebar ? 'fullWidthBlock' : ''}
    >
      <InnerArticleContainer $isSmall={getBlockContainerType(template) === 'small'}>
        {children}
      </InnerArticleContainer>
    </Root>
  );
};

export default BlockContainer;

const Root = styled.div<{ $hasSidebar: boolean; $isFullWidth: boolean }>`
  display: flex;
  justify-content: center;
  width: 100%;
  padding: ${({ $isFullWidth }) => ($isFullWidth ? 0 : '0 1.6')}rem;

  --sticky-bar-width: 3.8rem;
  --space-block-sticky: 5rem;
  --space-block-sidebar: 2.4rem;

  @media (min-width: ${Breakpoints.md}) {
    padding: ${({ $isFullWidth }) => ($isFullWidth ? 0 : '0 8')}rem;
  }

  @media (min-width: ${Breakpoints.lg}) {
    --margin-right-sticky: 4.8rem;
    --margin-left-sidebar: 4.8rem;
    --sidebar-width: 22%;
    --margin-right: ${({ $isFullWidth }) =>
      $isFullWidth
        ? 0
        : `calc(var(--sticky-bar-width) + var(--space-block-sticky) + var(--margin-right-sticky))`};
    --margin-left: ${({ $isFullWidth, $hasSidebar }) =>
      $isFullWidth
        ? 0
        : $hasSidebar
          ? `calc(var(--sidebar-width) + var(--space-block-sidebar) + var(--margin-left-sidebar))`
          : `calc(var(--margin-left-sidebar))`};

    margin-right: ${({ $isFullWidth, $hasSidebar }) =>
      !$isFullWidth && !$hasSidebar ? 'auto' : 'var(--margin-right)'};
    margin-left: ${({ $isFullWidth, $hasSidebar }) =>
      !$isFullWidth && !$hasSidebar ? 'auto' : 'var(--margin-left)'};
    width: calc(100% - var(--margin-left) - var(--margin-right));
    padding: 0;
  }

  @media (min-width: ${Breakpoints.xxl}) {
    --sidebar-width: 31.2rem;
    --full-width-sidebar-space: 13.6rem;
    --margin-right: ${({ $isFullWidth, $hasSidebar }) =>
      $hasSidebar
        ? `calc((100% - ${ContainerMaxWidth.xl})/2 + var(--sticky-bar-width) + var(--space-block-sticky) )`
        : $isFullWidth
          ? 0
          : `calc((100% - ${ContainerMaxWidth.xl} + var(--full-width-sidebar-space))/2 + var(--sticky-bar-width) + var(--space-block-sticky) )`};
    --margin-left: ${({ $hasSidebar, $isFullWidth }) =>
      $hasSidebar
        ? `calc(100% - ${ContainerMaxWidth.xl} - var(--margin-right) + var(--sidebar-width) + var(--sticky-bar-width) + var(--space-block-sticky) + var(--space-block-sidebar))`
        : $isFullWidth
          ? 0
          : `calc(100% - ${ContainerMaxWidth.xl} + var(--full-width-sidebar-space) - var(--margin-right) + var(--sticky-bar-width) + var(--space-block-sticky))`};

    margin-right: ${({ $isFullWidth, $hasSidebar }) =>
      !$isFullWidth && !$hasSidebar ? 'auto' : 'var(--margin-right)'};
    margin-left: ${({ $isFullWidth, $hasSidebar }) =>
      !$isFullWidth && !$hasSidebar ? 'auto' : 'var(--margin-left)'};
    width: calc(100% - var(--margin-left) - var(--margin-right));
    padding: 0;
  }
`;

const InnerArticleContainerMaxWidth = {
  md: '568px',
  lg: '648px',
};

export const InnerArticleContainer = styled.div<{ $isSmall: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.6rem;

  @media (min-width: ${Breakpoints.sm}) {
    & {
      max-width: 100%;
    }
  }
  @media (min-width: ${Breakpoints.md}) {
    & {
      max-width: ${({ $isSmall }) => ($isSmall ? InnerArticleContainerMaxWidth.md : '100%')};
    }
  }
  @media (min-width: ${Breakpoints.lg}) {
    & {
      max-width: ${({ $isSmall }) => ($isSmall ? InnerArticleContainerMaxWidth.lg : '100%')};
    }
  }
`;
